import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LandPlot, FolderKanban, Sprout, Images, FileText, Clock, Plus, Loader2, ExternalLink, Lock, Users, Receipt, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { uploadFile } from "../../lib/field";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, StatCard, StatusBadge, EmptyState, ResponsiveTable } from "../../components/dash/ui";
import { ActivityCard, MediaThumb } from "../../components/activity/ActivityCard";
import { ProjectCard } from "../projects/ProjectWorkspace";
import { Timeline } from "../activity/ActivityPages";
import { ACTIVITY_TYPES, COFFEE_TYPES, PROJECT_STAGES, formatDay, orgName, isManagerRole } from "../../lib/labels";
import { CoffeeProgress } from "../../components/coffee/CoffeeProgress";
import { BeforeAfter } from "../../components/coffee/PhotoHistory";
import { AddProjectButton } from "../lands/AddProjectPicker";
import { InvestorNotifications, MyNotifications } from "../../components/ManagerRequests";
import { MyBookkeeping } from "../../components/Bookkeeping";
import { AgreementArchives } from "../../components/AgreementArchives";
import { OverdueImbalanCard } from "../../components/dash/OverdueImbalanCard";
import { WeeklySummary } from "../../components/dash/WeeklySummary";
import { FundingSchemeCard } from "../../components/FundingScheme";

const InvestorFarmCard = ({ p, base }) => (
  <Link to={`${base}/proyek/${p.id}`} className="block bg-white border border-slate-200/80 rounded-xl p-5 hover:border-emerald-600/40 transition-colors" data-testid={`investor-farm-${p.id}`}>
    <div className="flex items-center justify-between gap-2"><span className="font-mono text-sm font-bold text-emerald-800">KEBUN {p.projectCode}</span><StatusBadge value={p.status} /></div>
    <p className="mt-1 font-semibold text-slate-900">Kebun Kopi {COFFEE_TYPES[p.coffeeType] || ""} · {Number(p.areaHectares ?? p.land?.areaHectares ?? 0).toLocaleString("id-ID", { maximumFractionDigits: 4 })} Ha</p>
    <p className="text-xs text-slate-500">{p.name} · Lahan {p.land?.landCode}</p>
    <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
      <div><dt className="text-xs text-slate-500">Target Pohon</dt><dd className="font-semibold" data-testid={`investor-farm-target-${p.id}`}>{p.targetTrees != null ? `±${p.targetTrees.toLocaleString("id-ID")}` : "-"}</dd></div>
      <div><dt className="text-xs text-slate-500">Populasi Saat Ini</dt><dd className="font-semibold">{p.currentTrees != null ? p.currentTrees.toLocaleString("id-ID") : "-"}</dd></div>
      <div><dt className="text-xs text-slate-500">Tahap</dt><dd className="font-semibold uppercase" data-testid={`investor-farm-stage-${p.id}`}>{PROJECT_STAGES[p.status]}</dd></div>
      <div><dt className="text-xs text-slate-500">Pengelola</dt><dd>{p.farmerGroupName}</dd></div>
      <div><dt className="text-xs text-slate-500">Lokasi</dt><dd>{[p.land?.village, p.land?.district, p.land?.regency].filter(Boolean).join(", ") || "-"}</dd></div>
      <div><dt className="text-xs text-slate-500">GPS</dt><dd className="font-mono text-xs" data-testid={`investor-farm-gps-${p.id}`}>{p.land?.latitude != null ? `${Number(p.land.latitude).toFixed(5)}, ${Number(p.land.longitude).toFixed(5)}` : "-"}</dd></div>
      <div><dt className="text-xs text-slate-500">Status Pendanaan</dt><dd>{p.projectType === "PRIVATE_MANAGED_COFFEE_PROJECT" ? "Kebun pribadi (tanpa investor)" : "Investor terdaftar"}</dd></div>
    </dl>
    <div className="mt-4"><CoffeeProgress status={p.status} testid={`investor-progress-${p.id}`} /></div>
    {p.latestPhotos?.length > 0 && <div className="mt-3 grid grid-cols-3 gap-2">{p.latestPhotos.map((m) => <img key={m.id} src={mediaSrc(m)} alt={m.caption || "Foto kebun"} className="aspect-square w-full object-cover rounded-md border" />)}</div>}
    <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
      <div><dt className="text-xs text-slate-500">Kegiatan Terakhir</dt><dd>{p.latestActivityType ? `${ACTIVITY_TYPES[p.latestActivityType]} · ${formatDay(p.latestActivityDate)}` : "-"}</dd></div>
      <div><dt className="text-xs text-slate-500">Status Laporan</dt><dd>{p.latestActivityStatus ? <StatusBadge value={p.latestActivityStatus} /> : "-"}</dd></div>
      <div><dt className="text-xs text-slate-500">Biaya Terbaru</dt><dd className="font-semibold">{p.latestExpense ? `${formatRupiah(p.latestExpense.amount)} · ${p.latestExpense.description}` : "-"}</dd></div>
      <div><dt className="text-xs text-slate-500">Video Terbaru</dt><dd>{p.latestVideo ? "Tersedia" : "Belum ada"}</dd></div>
      <div><dt className="text-xs text-slate-500">Masalah Terbuka</dt><dd className={p.openIssues ? "font-semibold text-red-700" : ""} data-testid={`investor-farm-issues-${p.id}`}>{p.openIssues || 0}</dd></div>
      <div><dt className="text-xs text-slate-500">Dokumen</dt><dd data-testid={`investor-farm-docs-${p.id}`}>{p.documentCount || 0} file</dd></div>
      <div><dt className="text-xs text-slate-500">Produksi / Panen Terakhir</dt><dd data-testid={`investor-farm-harvest-${p.id}`}>{p.latestHarvest ? `${ACTIVITY_TYPES[p.latestHarvest.activityType] || p.latestHarvest.activityType} · ${formatDay(p.latestHarvest.activityDate)}` : p.harvestStatus || "Belum ada"}</dd></div>
      <div><dt className="text-xs text-slate-500">Pembungaan / Buah</dt><dd>{[p.floweringStatus, p.fruitStatus].filter(Boolean).join(" · ") || "-"}</dd></div>
    </dl>
  </Link>
);
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordInput } from "../../components/dash/PasswordInput";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ROLE_HOME, ROLE_LABELS, DOC_TYPE_OPTIONS, formatDate, formatRupiah, mediaSrc } from "../../lib/labels";
import { OrgLogo, orgLogoSrc } from "../../components/dash/OrgLogo";
import { DevicesCard } from "../../components/auth/DevicesCard";

const Spinner = () => <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>;

const useGet = (url) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get(url).then((r) => setData(r.data)).catch((e) => { toast.error(formatApiError(e)); setData([]); });
  }, [url]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
};


export function MemberDashboard() {
  const { user } = useAuth();
  const [d] = useGet("/me/dashboard");
  const isFarmer = isManagerRole(user.role);
  const base = ROLE_HOME[user.role];
  if (!d) return <Spinner />;
  const sc = d.stageCounts || {};
  const cnt = (...k) => k.reduce((s, x) => s + (sc[x] || 0), 0);
  return (
    <div data-testid={isFarmer ? "farmer-dashboard" : "investor-dashboard"}>
      <PageHeader title={isFarmer ? (d.farmerGroup ? orgName(d.farmerGroup) : d.manager ? `${d.manager.fullName} · Pengelola Perorangan` : "Kelompok Tani") : `Selamat datang, ${user.name}`}
        subtitle={isFarmer ? (d.manager ? "Ruang kerja Pengelola Perorangan. Data di bawah ini hanya kebun yang ditugaskan kepada Anda." : "Ruang kerja kebun kopi kelompok Anda. Data di bawah ini hanya milik kelompok tani Anda.") : "Pantau pembangunan kebun kopi yang ditugaskan PT SJI kepada Anda (akses baca saja)."}
        action={isFarmer && <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">{user.role === "FARMER_GROUP" && <AddProjectButton className="w-full sm:w-auto" />}<Button asChild variant="outline" className="w-full sm:w-auto h-12 px-6 rounded-full border-emerald-800 text-emerald-800 font-bold" data-testid="dash-new-activity-btn"><Link to={`${base}/kegiatan/baru`}><Plus className="h-5 w-5 mr-1.5" />INPUT KEGIATAN KEBUN KOPI</Link></Button></div>} />
      {user.role === "INVESTOR" && <InvestorNotifications />}
      {user.role === "PRIVATE_LAND_OWNER" && <div className="mb-6"><MyNotifications testid="owner-notifications" /></div>}
      <MyBookkeeping />
      <AgreementArchives testid="my-archives" />
      {isFarmer && <OverdueImbalanCard base={base} />}
      {user.role === "INVESTOR" && <WeeklySummary />}
      {user.role === "INVESTOR" && <FundingSchemeCard />}
      {isFarmer ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          <StatCard label="Total Kebun Kopi Dikelola" value={d.projects} icon={FolderKanban} testid="stat-my-projects" />
          <StatCard label="Total Investor" value={d.investorsServed} icon={Users} testid="stat-investors-served" />
          <StatCard label="Total Hektare" value={Number(d.activeHectares || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 })} icon={LandPlot} testid="stat-active-ha" />
          <StatCard label="Lahan dalam Persiapan" value={cnt("DRAFT", "ACTIVE", "LAND_PREPARATION")} icon={LandPlot} testid="stat-stage-prep" />
          <StatCard label="Lahan dalam Penanaman" value={cnt("PLANTING")} icon={Sprout} testid="stat-stage-planting" />
          <StatCard label="Kebun dalam Perawatan" value={cnt("EARLY_GROWTH", "MAINTENANCE")} icon={Sprout} testid="stat-stage-maintenance" />
          <StatCard label="Kebun Menjelang Panen" value={cnt("PRE_PRODUCTION")} icon={Sprout} testid="stat-stage-preprod" />
          <StatCard label="Kebun Produktif" value={cnt("INITIAL_HARVEST", "PRODUCTIVE")} icon={Sprout} testid="stat-stage-productive" />
          <StatCard label="Kegiatan Bulan Ini" value={d.activitiesThisMonth} icon={Sprout} testid="stat-month-activities" />
          <StatCard label="Menunggu Verifikasi PT SJI" value={d.pendingVerification} icon={Clock} testid="stat-my-pending" />
          <StatCard label="Biaya Bulan Ini" value={formatRupiah(d.expensesThisMonth)} icon={Receipt} testid="stat-month-expenses" />
          <StatCard label="Masalah Kebun" value={d.openIssues + (d.revisionRequired || 0)} icon={AlertTriangle} hint={d.revisionRequired ? `${d.revisionRequired} kegiatan perlu perbaikan` : undefined} testid="stat-problems" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-5">
          <StatCard label="Kebun Kopi Saya" value={d.projects} icon={FolderKanban} testid="stat-my-projects" />
          <StatCard label="Terverifikasi PT SJI" value={d.verifiedActivities} icon={Sprout} testid="stat-verified" />
          <StatCard label="Menunggu Verifikasi" value={d.pendingVerification} icon={Clock} testid="stat-my-pending" />
          <StatCard label="Foto & Video" value={d.media} icon={Images} testid="stat-my-media" />
          <StatCard label="Dokumen" value={d.documents} icon={FileText} testid="stat-my-documents" />
        </div>
      )}
      <div className="mt-10">
        <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-4">{isFarmer ? "Kebun Kopi Terbaru" : "Kebun Kopi Saya"}</h2>
        {d.recentProjects.length ? <div className="grid gap-5 md:grid-cols-2">{d.recentProjects.map((p) => (isFarmer ? <ProjectCard key={p.id} p={p} base={base} farmer /> : <div key={p.id} className="space-y-2"><InvestorFarmCard p={p} base={base} /><BeforeAfter projectId={p.id} /></div>))}</div>
          : <EmptyState text="Belum ada kebun kopi." testid="recent-projects-empty" />}
      </div>
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base md:text-lg font-semibold text-slate-900">{isFarmer ? "Kegiatan Kebun Terbaru" : "Update Terbaru Kebun Kopi Saya"}</h2>
          <Link to={`${base}/kegiatan`} className="text-sm font-semibold text-emerald-800" data-testid="view-all-updates-link">Lihat semua</Link>
        </div>
        {d.recentActivities.length ? <div className="grid gap-4 md:grid-cols-2" data-testid="dash-recent-activities">{d.recentActivities.map((a) => <ActivityCard key={a.id} a={a} base={base} />)}</div>
          : <EmptyState text="Belum ada kegiatan kebun kopi yang dilaporkan." testid="recent-updates-empty" />}
      </div>
      {isFarmer && (
        <div className="mt-10">
          <h2 className="text-base md:text-lg font-semibold text-slate-900 mb-4">Menunggu Verifikasi & Perlu Perbaikan</h2>
          <Timeline status="SUBMITTED,UNDER_REVIEW,REVISION_REQUIRED" testid="dash-pending" />
        </div>
      )}
    </div>
  );
}

export function MyLand() {
  const [rows] = useGet("/me/land");
  return (
    <div data-testid="my-land-page">
      <PageHeader title="Lahan Saya" subtitle="Lahan yang terdaftar atas nama kelompok tani Anda." />
      {!rows ? <Spinner /> : (
        <ResponsiveTable testid="my-land-table" rows={rows} empty="Belum ada lahan terdaftar."
          columns={[{ key: "landCode", label: "Kode", render: (r) => <span className="font-mono text-xs">{r.landCode}</span> }, { key: "name", label: "Nama" },
            { key: "areaHectares", label: "Luas (ha)" },
            { key: "loc", label: "Koordinat", render: (r) => (r.latitude != null ? `${r.latitude}, ${r.longitude}` : "Belum diisi") },
            { key: "verificationStatus", label: "Verifikasi", render: (r) => <StatusBadge value={r.verificationStatus} /> }]} />
      )}
    </div>
  );
}

export function MyProjects() {
  const { user } = useAuth();
  const [rows] = useGet("/me/projects");
  const isFarmer = user.role === "FARMER_GROUP";
  return (
    <div data-testid="my-projects-page">
      <PageHeader title="Kebun Saya" subtitle={isFarmer ? "Rencana Kebun dan kebun kopi yang dikelola kelompok Anda. Rencana Kebun baru dibuat dari lahan yang sudah didaftarkan." : "Rencana Kebun dan kebun yang dapat Anda akses."}
        action={isFarmer && <AddProjectButton />} />
      {!rows ? <Spinner /> : !rows.length ? <EmptyState text={isFarmer ? "Belum ada kebun kopi. Klik TAMBAH RENCANA KEBUN untuk membuat dari lahan Anda." : "Belum ada kebun kopi yang ditugaskan."} testid="my-projects-empty" /> : (
        <div className="grid gap-5 md:grid-cols-2">
          {rows.map((p) => <ProjectCard key={p.id} p={p} base={ROLE_HOME[user.role]} farmer={isFarmer} />)}
        </div>
      )}
    </div>
  );
}

const MediaUploadDialog = ({ onClose, onDone, initialProject }) => {
  const [projects] = useGet("/me/projects");
  const [projectId, setProjectId] = useState(initialProject || "");
  const [caption, setCaption] = useState("");
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error("Pilih minimal 1 file");
    setSaving(true);
    try {
      for (const [i, file] of files.entries()) {
        await uploadFile("/media/upload", file, { projectId, caption }, (p) => setProgress(`File ${i + 1}/${files.length}: ${p}%`));
      }
      toast.success("Terunggah. Menunggu verifikasi PT SJI.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="media-form-dialog">
        <DialogHeader><DialogTitle>Upload Foto / Video</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <select required value={projectId} onChange={(e) => setProjectId(e.target.value)} className="flex h-12 w-full rounded-md border border-input bg-white px-3 text-base" data-testid="media-form-project">
            <option value="">- Pilih kebun -</option>
            {(projects || []).map((p) => <option key={p.id} value={p.id}>{p.projectCode} - {p.name}</option>)}
          </select>
          <Input placeholder="Keterangan" value={caption} onChange={(e) => setCaption(e.target.value)} className="h-12 text-base" data-testid="media-form-caption" />
          <Input type="file" accept="image/*,video/*" multiple onChange={(e) => setFiles(Array.from(e.target.files))} className="h-12" data-testid="media-form-file" />
          {progress && <p className="text-xs text-slate-500" data-testid="media-form-progress">{progress}</p>}
          <DialogFooter><Button type="submit" disabled={saving} className="h-12 w-full bg-emerald-800 hover:bg-emerald-900" data-testid="media-form-submit-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Upload {files.length ? `(${files.length})` : ""}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};


export function MediaGallery() {
  const { user } = useAuth();
  const isFarmer = isManagerRole(user.role);
  const [rows, load] = useGet("/media");
  const [params] = useSearchParams();
  const [open, setOpen] = useState(!!params.get("projectId") && isFarmer);
  return (
    <div data-testid="media-gallery-page">
      <PageHeader title="Foto & Video" subtitle={isFarmer ? "Dokumentasi lapangan kelompok Anda." : "Dokumentasi kebun Anda beserta status verifikasi PT SJI."}
        action={isFarmer && <Button onClick={() => setOpen(true)} className="h-12 px-6 bg-emerald-800 hover:bg-emerald-900 rounded-full" data-testid="new-media-btn"><Plus className="h-4 w-4 mr-1.5" /> Upload Foto/Video</Button>} />
      {!rows ? <Spinner /> : !rows.length ? <EmptyState text="Belum ada foto atau video kebun kopi." testid="media-empty" /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="media-grid">
          {rows.map((m) => (
            <figure key={m.id} className="bg-white border border-slate-200/80 rounded-xl overflow-hidden" data-testid={`media-item-${m.id}`}>
              <div className="w-full aspect-[4/3] bg-slate-50"><MediaThumb m={m} /></div>
              <figcaption className="p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs text-emerald-700">{m.projectCode}</span>
                  <StatusBadge value={m.verificationStatus} />
                </div>
                <p className="mt-2 text-sm text-slate-700">{m.caption || "-"}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
      {open && <MediaUploadDialog initialProject={params.get("projectId")} onClose={() => setOpen(false)} onDone={() => { setOpen(false); load(); }} />}
    </div>
  );
}

export function Documents() {
  const [rows] = useGet("/me/documents");
  return (
    <div data-testid="documents-page">
      <PageHeader title="Dokumen" subtitle="Dokumen kebun yang dibagikan PT SJI kepada Anda." />
      {!rows ? <Spinner /> : (
        <ResponsiveTable testid="documents-table" rows={rows} empty="Belum ada dokumen."
          columns={[{ key: "title", label: "Judul" }, { key: "projectCode", label: "Kode Kebun" },
            { key: "documentType", label: "Jenis", render: (r) => DOC_TYPE_OPTIONS.find((o) => o.value === r.documentType)?.label },
            { key: "createdAt", label: "Tanggal", render: (r) => formatDate(r.createdAt) },
            { key: "fileUrl", label: "File", render: (r) => <a href={mediaSrc(r)} target="_blank" rel="noopener noreferrer" className="text-emerald-800 font-semibold text-xs inline-flex items-center gap-1" data-testid={`doc-open-${r.id}`}>Buka <ExternalLink className="h-3 w-3" /></a> }]} />
      )}
    </div>
  );
}

const PasswordForm = () => {
  const [f, setF] = useState({ currentPassword: "", newPassword: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/change-password", f);
      toast.success("Kata sandi diperbarui");
      setF({ currentPassword: "", newPassword: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={save} className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-6 space-y-4" data-testid="change-password-form">
      <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Lock className="h-4 w-4" /> Ubah Kata Sandi</h2>
      <p className="text-xs text-slate-500" data-testid="password-change-notice">Setiap perubahan kata sandi tercatat dan dilaporkan ke PT SJI sebagai pemilik platform.</p>
      <div className="space-y-1.5"><Label htmlFor="cur">Kata sandi saat ini</Label><PasswordInput id="cur" required autoComplete="current-password" value={f.currentPassword} onChange={(e) => setF({ ...f, currentPassword: e.target.value })} data-testid="current-password-input" /></div>
      <div className="space-y-1.5"><Label htmlFor="new">Kata sandi baru</Label><PasswordInput id="new" minLength={8} autoComplete="new-password" required value={f.newPassword} onChange={(e) => setF({ ...f, newPassword: e.target.value })} data-testid="new-password-input" /></div>
      <Button type="submit" disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid="change-password-btn">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan</Button>
    </form>
  );
};

const OrgLogoCard = ({ group, onSaved }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = (f) => {
    if (f && f.size > 10 * 1024 * 1024) { toast.error("Logo terlalu besar (maks 10 MB)"); return; }
    setFile(f); setPreview(f ? URL.createObjectURL(f) : null);
  };
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); pick(null); onSaved(); window.dispatchEvent(new Event("org-updated")); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const current = orgLogoSrc(group);
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-6 space-y-4 lg:col-span-2" data-testid="org-logo-card">
      <div>
        <h3 className="font-bold text-slate-900">Logo Kelompok / Koperasi Tani</h3>
        <p className="text-xs text-slate-500 mt-1">Logo tampil di dashboard Anda, di halaman profil publik organisasi, dan di daftar Pengguna serta Kelompok/Koperasi Tani milik PT SJI. Format JPG, PNG, atau WEBP, maks 10 MB.</p>
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <OrgLogo src={preview || current} size="h-24 w-24" testid="org-logo-preview" />
        <div className="space-y-2">
          {preview && <p className="text-xs text-amber-700">Pratinjau — belum disimpan</p>}
          <div className="flex flex-wrap gap-2">
            <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-800 border border-emerald-200 rounded-md px-3 py-2 cursor-pointer hover:bg-emerald-50">
              <Images className="h-4 w-4" />{current ? "Ganti Logo" : "Pilih Logo"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { pick(e.target.files?.[0] || null); e.target.value = ""; }} data-testid="org-logo-input" />
            </label>
            {file && <Button type="button" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" onClick={() => run(() => uploadFile("/me/farmer-group/logo", file), "Logo disimpan")} data-testid="org-logo-save">{busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Simpan Logo</Button>}
            {file && <Button type="button" variant="outline" disabled={busy} onClick={() => pick(null)} data-testid="org-logo-cancel">Batal</Button>}
            {current && !file && <Button type="button" variant="outline" disabled={busy} className="text-red-700" onClick={() => run(() => api.delete("/me/farmer-group/logo"), "Logo dihapus")} data-testid="org-logo-delete">Hapus Logo</Button>}
          </div>
        </div>
      </div>
    </div>
  );
};

export function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user.name);
  const [group, setGroup] = useState(null);
  const loadGroup = useCallback(() => api.get("/me/farmer-group").then((r) => setGroup(r.data)).catch(() => {}), []);
  useEffect(() => {
    if (user.role === "FARMER_GROUP") loadGroup();
  }, [user.role, loadGroup]);
  const [mgr, setMgr] = useState(null);
  useEffect(() => {
    if (user.role === "INDIVIDUAL_MANAGER") api.get("/individual-managers/me").then((r) => setMgr(r.data)).catch(() => {});
  }, [user.role]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch("/auth/me", { name });
      setUser(data);
      toast.success("Profil diperbarui");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div data-testid="profile-page">
      <PageHeader title="Profil" subtitle="Informasi akun Anda." />
      <div className="grid gap-6 lg:grid-cols-2 max-w-4xl">
        <form onSubmit={save} className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-6 space-y-4" data-testid="profile-form">
          <div className="space-y-1.5"><Label htmlFor="pname">Nama</Label><Input id="pname" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" /></div>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Email</dt><dd data-testid="profile-email">{user.email}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-slate-500">Peran</dt><dd data-testid="profile-role">{ROLE_LABELS[user.role]}</dd></div>
            {group && <div className="flex justify-between gap-3"><dt className="text-slate-500">Organisasi</dt><dd data-testid="profile-group"><Link to={`/kelompok-tani/${group.id}`} className="font-semibold text-emerald-800">{orgName(group)}</Link> · Kolaborasi dengan PT SJI</dd></div>}
          </dl>
          <Button type="submit" className="bg-emerald-800 hover:bg-emerald-900" data-testid="profile-save-btn">Simpan</Button>
        </form>
        <PasswordForm />
        <DevicesCard />
        {group && <OrgLogoCard group={group} onSaved={loadGroup} />}
        {mgr && (
          <div className="bg-white border border-slate-200/80 rounded-xl p-5 sm:p-6 text-sm space-y-1.5 lg:col-span-2" data-testid="manager-profile-card">
            <p className="font-bold text-slate-900">{mgr.fullName} · Pengelola Perorangan · <span className="text-emerald-800">{mgr.statusLabel}</span></p>
            <p>Pengalaman {mgr.yearsExperience} tahun · Wilayah {mgr.workRegion}</p>
            <p data-testid="manager-profile-capacity">Kapasitas {mgr.maxHectares} ha / {mgr.maxActiveProjects} kebun · Sedang dikelola {mgr.activeHectares} ha / {mgr.activeProjects} kebun · Sisa {mgr.remainingHectares} ha {mgr.capacityFull && <b className="text-red-700">· Kapasitas Penuh</b>}</p>
            <p className="text-xs text-slate-500">{mgr.responsibility}</p>
          </div>
        )}
      </div>
    </div>
  );
}
